import React, { useState, useEffect, useCallback } from 'react';
import { useMessages } from './AppMessages';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faSpinner, faXmark, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import './ExportNotification.scss';
import { EXPORT_QUEUED_EVENT } from '../explorers/baseExplorer/downloadExportCsv';

interface ExportJob {
  // Field names mirror the /api/export/jobs response rows (#688).
  id: string;
  entity: string;
  status: 'pending' | 'processing' | 'complete' | 'failed';
  record_count: number;
  created_at: string;
  error?: string;
}

export interface ExportNotificationProps {}

const ExportNotification: React.FC<ExportNotificationProps> = () => {
  const { showMessage } = useMessages();
  const [jobs, setJobs] = useState<ExportJob[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [downloading, setDownloading] = useState<Set<string>>(new Set());

  useEffect(() => {
    const poll = async () => {
      try {
        const res = await fetch('/api/export/jobs', { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          const recent: ExportJob[] = (data.jobs as ExportJob[]).filter(
            (j) =>
              new Date(j.created_at) > new Date(Date.now() - 3600000) &&
              (j.status === 'pending' ||
                j.status === 'processing' ||
                j.status === 'complete' ||
                j.status === 'failed'),
          );
          setJobs(recent);
        }
      } catch {
        // Network error — silently ignore, will retry next interval
      }
    };

    poll();
    const interval = setInterval(poll, 10000);
    window.addEventListener(EXPORT_QUEUED_EVENT, poll);
    return () => {
      clearInterval(interval);
      window.removeEventListener(EXPORT_QUEUED_EVENT, poll);
    };
  }, []);

  const handleDismiss = useCallback((jobId: string) => {
    // Hide it straight away — the poll is on a ten-second interval and the user
    // has asked for it gone.
    setDismissed((prev) => new Set(prev).add(jobId));

    // #864 — and record it on the server. Dismissal used to live only in this
    // tab, so the job came back as a new notification on the next login. A
    // failure here is not worth interrupting the user for: the notice stays
    // dismissed for this session and simply returns later, which is the old
    // behaviour rather than a new one.
    fetch(`/api/export/jobs/${jobId}/acknowledge`, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {
      /* see above */
    });
  }, []);

  // Auto-dismiss failed jobs after 8 seconds.
  //
  // #869 — the timers are collected and cleared from the effect's OWN cleanup.
  // They used to be scheduled inside a forEach whose callback returned the
  // clearTimeout, which React never sees: nothing was ever cleared, the poll
  // re-ran this effect every ten seconds and scheduled another, and they fired
  // after unmount.
  useEffect(() => {
    const timers = jobs
      .filter((j) => j.status === 'failed' && !dismissed.has(j.id))
      .map((j) =>
        setTimeout(() => {
          // #864 — auto-dismiss is still a dismissal, so it has to persist too,
          // or a failed export returns for the rest of the hour.
          handleDismiss(j.id);
        }, 8000),
      );

    return () => timers.forEach((timer) => clearTimeout(timer));
  }, [jobs, dismissed, handleDismiss]);

  const handleDownload = useCallback(
    async (job: ExportJob) => {
      if (downloading.has(job.id)) return;
      setDownloading((prev) => new Set(prev).add(job.id));

      try {
        const res = await fetch(`/api/export/jobs/${job.id}/download`, {
          credentials: 'include',
        });
        if (!res.ok) {
          showMessage('Download failed. Please try again.', 'error');
          return;
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const dateStamp = new Date().toISOString().slice(0, 10);
        a.download = `${job.entity}_export_${dateStamp}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        handleDismiss(job.id);
      } catch {
        showMessage('Download request failed.', 'error');
      } finally {
        setDownloading((prev) => {
          const next = new Set(prev);
          next.delete(job.id);
          return next;
        });
      }
    },
    [downloading, handleDismiss],
  );

  const visibleJobs = jobs.filter((j) => !dismissed.has(j.id));

  if (visibleJobs.length === 0) return null;

  return (
    <div className="export-notification-stack" role="region" aria-label="Export notifications">
      {visibleJobs.map((job) => (
        <div
          key={job.id}
          className={`export-notification-card export-notification-card--${job.status}`}
          role="status"
        >
          {(job.status === 'pending' || job.status === 'processing') && (
            <>
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                className="export-notification-icon export-notification-icon--spinner"
                aria-hidden="true"
              />
              <span className="export-notification-text">
                Preparing{' '}
                <strong>{job.entity}</strong> export
                {job.record_count > 0 && ` (${job.record_count.toLocaleString()} records)`}
                &hellip;
              </span>
            </>
          )}

          {job.status === 'complete' && (
            <>
              <FontAwesomeIcon
                icon={faDownload}
                className="export-notification-icon export-notification-icon--ready"
                aria-hidden="true"
              />
              <span className="export-notification-text">
                Export ready &mdash; <strong>{job.entity}</strong>
                {job.record_count > 0 && ` (${job.record_count.toLocaleString()} records)`}
              </span>
              <div className="export-notification-actions">
                <button
                  className="export-notification-btn export-notification-btn--download"
                  onClick={() => handleDownload(job)}
                  disabled={downloading.has(job.id)}
                  aria-label={`Download ${job.entity} export`}
                >
                  {downloading.has(job.id) ? (
                    <FontAwesomeIcon icon={faSpinner} spin aria-hidden="true" />
                  ) : (
                    'Download'
                  )}
                </button>
                <button
                  className="export-notification-btn export-notification-btn--dismiss"
                  onClick={() => handleDismiss(job.id)}
                  aria-label="Dismiss notification"
                >
                  <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
                </button>
              </div>
            </>
          )}

          {job.status === 'failed' && (
            <>
              <FontAwesomeIcon
                icon={faCircleExclamation}
                className="export-notification-icon export-notification-icon--error"
                aria-hidden="true"
              />
              <span className="export-notification-text">
                Export failed{job.error ? `: ${job.error}` : ' — please try again.'}
              </span>
              <button
                className="export-notification-btn export-notification-btn--dismiss"
                onClick={() => handleDismiss(job.id)}
                aria-label="Dismiss error notification"
              >
                <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
              </button>
            </>
          )}
        </div>
      ))}
    </div>
  );
};

export { ExportNotification };
export default ExportNotification;
